/**
 * Chart Data Stream Parser
 * Parses streaming chart JSON data from testcases section
 */

class TestcaseParser {
  constructor() {
    this.state = {
      buffer: "",
      processingState: 'idle',
      chartData: null
    };
  }

  // =================================================================
  // PUBLIC API (used by App.js)
  // =================================================================

  processStreamContent(params) {
    const { content, section } = params;

    if (section !== "testcases") {
      return {
        isSuccessful: false,
        chartData: null
      };
    }

    // If content is empty but we already have parsed data, return it
    if (!content && this.state.chartData) {
      console.log('📊 Chart Parser: Returning already parsed chart data (no new content)');
      return {
        isSuccessful: true,
        chartData: this.state.chartData
      };
    }

    // If content is empty and no parsed data, return null
    if (!content) {
      return {
        isSuccessful: false,
        chartData: null
      };
    }

    this.state.processingState = 'processing';
    this.state.buffer += content;

    try {
      // Try to parse chart data from buffer
      this.parseChartData(this.state.buffer);

      if (this.state.chartData) {
        this.state.processingState = 'completed';
        return {
          isSuccessful: true,
          chartData: this.state.chartData
        };
      }

      this.state.processingState = 'idle';
      return {
        isSuccessful: false,
        chartData: null
      };

    } catch (error) {
      console.error('📊 Chart Parser: Error processing content:', error);
      this.state.processingState = 'error';
      return {
        isSuccessful: false,
        chartData: null
      };
    }
  }

  resetState() {
    console.log('🔄 Chart Parser: Resetting state');

    this.state = {
      buffer: "",
      processingState: 'idle',
      chartData: null
    };
  }

  // =================================================================
  // CHART PARSING LOGIC
  // =================================================================

  parseChartData(buffer) {
    // Only parse if we haven't already successfully parsed chart data
    if (this.state.chartData) {
      return;
    }

    // Check if buffer contains "series" key
    const seriesIndex = buffer.indexOf('"series"');
    if (seriesIndex === -1) {
      return;
    }

    try {
      // Find opening brace before "series"
      let openBraceIndex = -1;
      for (let i = seriesIndex; i >= 0; i--) {
        if (buffer[i] === '{') {
          openBraceIndex = i;
          break;
        }
      }

      if (openBraceIndex === -1) {
        console.log('📊 Chart Parser: Opening brace not found before "series"');
        return;
      }

      // Find matching closing brace
      let braceCount = 0;
      let closeBraceIndex = -1;
      for (let i = openBraceIndex; i < buffer.length; i++) {
        if (buffer[i] === '{') braceCount++;
        if (buffer[i] === '}') {
          braceCount--;
          if (braceCount === 0) {
            closeBraceIndex = i;
            break;
          }
        }
      }

      if (closeBraceIndex === -1) {
        console.log('📊 Chart Parser: JSON not yet complete, waiting for closing brace...');
        return;
      }

      // Extract and validate JSON string
      const jsonString = buffer.substring(openBraceIndex, closeBraceIndex + 1);

      // Validate brackets are balanced
      const openBrackets = (jsonString.match(/\[/g) || []).length;
      const closeBrackets = (jsonString.match(/\]/g) || []).length;

      if (openBrackets !== closeBrackets) {
        console.log('📊 Chart Parser: JSON brackets not balanced yet, waiting...');
        return;
      }

      // Parse JSON
      const parsed = JSON.parse(jsonString);

      // Validate structure
      if (parsed.series && Array.isArray(parsed.series) && parsed.series.length > 0) {
        console.log('✅ Chart Parser: Chart data parsed successfully! Series count:', parsed.series.length);
        this.state.chartData = parsed;
      } else if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].name && parsed[0].data) {
        // Format 2: direct array of series
        console.log('✅ Chart Parser: Chart data parsed (Format 2)! Series count:', parsed.length);
        this.state.chartData = { series: parsed };
      } else {
        console.warn('📊 Chart Parser: JSON found but series array is invalid:', parsed);
      }
    } catch (e) {
      // Silent during streaming - it's expected that JSON might be incomplete
      console.log('📊 Chart Parser: Chart parsing in progress...', e.message);
    }
  }
}

export default TestcaseParser;
